import { SSRKeycloakProvider } from "@react-keycloak/ssr";
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AuthClientTokens } from "@react-keycloak/core";
import { usePortalProvider } from "./portal-provider";
import { WebCookiePersistor } from "../util/web-cookie-persistor";
const persistor = new WebCookiePersistor();

interface PortalAuthContext {
  accessToken?: string;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<PortalAuthContext>({
  login: () => null,
  logout: () => null,
});

export const KeycloakProvider: FC = ({ children }) => {
  const {
    accessToken: portalAccessToken,
    env,
    setAccessToken: setPortalAccessToken,
  } = usePortalProvider();
  const [accessToken, setAccessToken] = useState<string | undefined>(
    portalAccessToken
  );

  useEffect(() => {
    setAccessToken(portalAccessToken);
  }, [portalAccessToken]);

  const updateToken = useCallback(
    (data: AuthClientTokens) => {
      if (!data.token) {
        return;
      }
      setAccessToken(data.token);
      setPortalAccessToken(data.token);
    },
    [setPortalAccessToken]
  );

  const login = useCallback(() => {
    document.location.href = `/login?redirectUrl=${encodeURIComponent(
      window.location.href
    )}`;
  }, []);

  const logout = useCallback(() => {
    document.location.href = `/logout?redirectUrl=${encodeURIComponent(
      window.location.origin
    )}`;
  }, []);

  const globalLoginUrl =
    typeof window !== "undefined" ? window?.SMARTPASS_LOGIN_URL : undefined;

  const globalLoginRealm =
    typeof window !== "undefined" ? window?.SMARTPASS_LOGIN_REALM : "";

  const globalLoginClientId =
    typeof window !== "undefined" ? window?.SMARTPASS_LOGIN_CLIENT_ID : "";

  return (
    <SSRKeycloakProvider
      keycloakConfig={{
        url: env?.SMARTPASS_LOGIN_URL || globalLoginUrl,
        realm: env?.SMARTPASS_LOGIN_REALM || globalLoginRealm,
        clientId: env?.SMARTPASS_LOGIN_CLIENT_ID || globalLoginClientId,
      }}
      persistor={persistor}
      onTokens={updateToken}
      initOptions={{
        onLoad: "check-sso",
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        responseMode: "query",
        flow: "standard",
        silentCheckSsoRedirectUri:
          typeof window !== "undefined"
            ? window.location.origin + "/silent-check-sso.html"
            : undefined,
      }}
    >
      <AuthContext.Provider value={{ accessToken, login, logout }}>
        {children}
      </AuthContext.Provider>
    </SSRKeycloakProvider>
  );
};

export function usePortalAuth(): PortalAuthContext {
  return useContext(AuthContext);
}
